/**
 * Seitenbearbeitung mit lokalem Entwurf und idempotentem Speichern (Paket 14).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { schluessel } from "@/lib/abfragen";
import { useIch } from "@/lib/haken";
import {
  AUTOSAVE,
  entscheideRecovery,
  liesEntwurf,
  liesOderErzeugeGeraetId,
  loescheEntwurf,
  schreibeEntwurf,
  type SpeicherStatus,
} from "@/lib/seiten-entwurf";
import { SeitenEditor } from "@/components/seiten/SeitenEditor";
import { SeitenLeseansicht } from "@/components/seiten/SeitenLeseansicht";
import { Button } from "@/components/Button";
import { KommentareFaden } from "@/components/KommentareFaden";
import { pfadProjektSeiten } from "@/lib/projekt-routen";
import { darfAendern } from "@/lib/rollen";
import type { Rolle } from "@cubic/shared";
import { Reiter, Reiterleiste } from "@/components/entwurf/Reiter";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { seitenTextStatistik } from "@/lib/seiten-inhalt";

type SeitenModus = "lesen" | "bearbeiten" | "geteilt";

export function SeiteDetail() {
  const { projektId, seiteId } = useParams({
    from: "/_app/projekte/$projektId/seiten/$seiteId",
  });
  const ich = useIch();
  const client = useQueryClient();
  const personId = ich.data?.personId;

  const seite = useQuery({
    queryKey: schluessel.seite(seiteId),
    queryFn: () => api.seiten.lies.query({ id: seiteId }),
    enabled: Boolean(seiteId),
    // Fokus-Refetch aktualisiert nur den bekannten Serverbasisstand —
    // der Editorinhalt wird nicht zurückgesetzt (staleTime + kein placeholder reset).
    refetchOnWindowFocus: true,
  });

  const fassungen = useQuery({
    queryKey: schluessel.seitenFassungen(seiteId),
    queryFn: () => api.seiten.fassungen.query({ seiteId }),
    enabled: Boolean(seiteId),
  });

  const status = useQuery({
    queryKey: schluessel.seitenVersionierungsstatus(seiteId),
    queryFn: () => api.seiten.versionierungsstatus.query({ seiteId }),
    enabled: Boolean(seiteId),
  });

  const [modus, setzeModus] = useState<SeitenModus>("lesen");
  const [titel, setzeTitel] = useState("");
  const [inhalt, setzeInhalt] = useState("");
  const [basisVersion, setzeBasisVersion] = useState(1);
  const [speicher, setzeSpeicher] = useState<SpeicherStatus>("gespeichert");
  const [banner, setzeBanner] = useState<string | null>(null);
  const [bereit, setzeBereit] = useState(false);

  const geraetId = useRef<string | null>(null);
  const lokaleRevision = useRef(0);
  const inhaltRef = useRef("");
  const titelRef = useRef("");
  const basisVersionRef = useRef(1);
  const speicherLaufend = useRef(false);
  const serverNeuPlanen = useRef(false);
  const sendeServerRef = useRef<(() => Promise<void>) | null>(null);
  const initialisiertFuer = useRef<string | null>(null);
  const lokalTimer = useRef<number | null>(null);
  const lokalMaxTimer = useRef<number | null>(null);
  const serverTimer = useRef<number | null>(null);
  const serverMaxTimer = useRef<number | null>(null);

  // Initial laden + Recovery
  useEffect(() => {
    if (!seite.data || !personId || initialisiertFuer.current === seiteId) {
      return;
    }
    initialisiertFuer.current = seiteId;
    let abgebrochen = false;
    void (async () => {
      try {
        geraetId.current = await liesOderErzeugeGeraetId();
        const entwurf = await liesEntwurf(personId, seiteId);
        const serverBasis = {
          version: seite.data.version,
          titel: seite.data.titel,
          inhalt: seite.data.inhalt,
          fingerabdruck: `${seite.data.version}`,
        };
        const entscheidung = entscheideRecovery({
          entwurf,
          server: serverBasis,
          serverErreichbar: true,
          zugriff: seite.data.projektId === projektId,
        });

        if (abgebrochen) return;

        if (seite.data.projektId !== projektId) {
          setzeBanner("Diese Seite gehört nicht zu diesem Projekt.");
          setzeBereit(true);
          return;
        }

        switch (entscheidung.art) {
          case "lokal_wiederherstellen":
            setzeTitel(entwurf!.titel);
            setzeInhalt(entwurf!.inhalt);
            titelRef.current = entwurf!.titel;
            inhaltRef.current = entwurf!.inhalt;
            lokaleRevision.current = entwurf!.lokaleRevision;
            setzeBasisVersion(entwurf!.basis.version);
            basisVersionRef.current = entwurf!.basis.version;
            setzeBanner("Lokaler Entwurf wiederhergestellt.");
            setzeModus("bearbeiten");
            break;
          case "konflikt":
            setzeTitel(entwurf!.titel);
            setzeInhalt(entwurf!.inhalt);
            titelRef.current = entwurf!.titel;
            inhaltRef.current = entwurf!.inhalt;
            lokaleRevision.current = entwurf!.lokaleRevision;
            setzeBasisVersion(entwurf!.basis.version);
            basisVersionRef.current = entwurf!.basis.version;
            setzeSpeicher("konflikt");
            setzeBanner(
              `Konflikt: Server ist bei Version ${entscheidung.serverVersion}. Entwurf bleibt erhalten.`,
            );
            setzeModus("bearbeiten");
            break;
          case "ausstehend_wiederholen":
            setzeTitel(entwurf!.titel);
            setzeInhalt(entwurf!.inhalt);
            titelRef.current = entwurf!.titel;
            inhaltRef.current = entwurf!.inhalt;
            lokaleRevision.current = entwurf!.lokaleRevision;
            setzeBasisVersion(entwurf!.basis.version);
            basisVersionRef.current = entwurf!.basis.version;
            setzeModus("bearbeiten");
            setzeBanner("Offene Speicherung wird wiederholt …");
            break;
          case "entwurf_loeschen":
            await loescheEntwurf(personId, seiteId);
            setzeTitel(seite.data.titel);
            setzeInhalt(seite.data.inhalt);
            titelRef.current = seite.data.titel;
            inhaltRef.current = seite.data.inhalt;
            setzeBasisVersion(seite.data.version);
            basisVersionRef.current = seite.data.version;
            break;
          default:
            setzeTitel(seite.data.titel);
            setzeInhalt(seite.data.inhalt);
            titelRef.current = seite.data.titel;
            inhaltRef.current = seite.data.inhalt;
            setzeBasisVersion(seite.data.version);
            basisVersionRef.current = seite.data.version;
        }
        setzeBereit(true);
      } catch {
        setzeTitel(seite.data.titel);
        setzeInhalt(seite.data.inhalt);
        setzeBasisVersion(seite.data.version);
        basisVersionRef.current = seite.data.version;
        setzeSpeicher("nicht_sicher");
        setzeBanner("Lokaler Speicher nicht verfügbar.");
        setzeBereit(true);
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [seite.data, personId, seiteId, projektId]);

  const speichernMutation = useMutation({
    mutationFn: async (payload: {
      aenderungsId: string;
      lokaleRevision: number;
      titel: string;
      inhalt: string;
      erwarteteVersion: number;
    }) => {
      if (!geraetId.current) throw new Error("Keine Geräte-ID");
      return api.seiten.speichern.mutate({
        id: seiteId,
        titel: payload.titel,
        inhalt: payload.inhalt,
        erwarteteVersion: payload.erwarteteVersion,
        geraetId: geraetId.current,
        lokaleRevision: payload.lokaleRevision,
        aenderungsId: payload.aenderungsId,
      });
    },
    onSuccess: async (erg, vars) => {
      // Auch eine inzwischen veraltete Bestätigung hat die Serverversion erhöht.
      // Der unmittelbar folgende Stand muss deshalb auf dieser Version aufbauen.
      basisVersionRef.current = erg.version;
      setzeBasisVersion(erg.version);
      if (vars.lokaleRevision !== lokaleRevision.current) {
        setzeSpeicher("lokal_wird_gesichert");
        return;
      }
      setzeSpeicher("gespeichert");
      if (personId) {
        await loescheEntwurf(personId, seiteId);
      }
      await client.invalidateQueries({ queryKey: schluessel.seite(seiteId) });
      await client.invalidateQueries({
        queryKey: schluessel.seitenFassungen(seiteId),
      });
      await client.invalidateQueries({
        queryKey: schluessel.seitenVersionierungsstatus(seiteId),
      });
    },
    onError: () => {
      setzeSpeicher("offline");
    },
  });

  const sichereLokal = useCallback(async () => {
    if (!personId || !geraetId.current) return;
    setzeSpeicher((s) => (s === "konflikt" ? s : "lokal_wird_gesichert"));
    try {
      await schreibeEntwurf({
        schemaVersion: 1,
        personId,
        projektId,
        seiteId,
        geraetId: geraetId.current,
        titel: titelRef.current,
        inhalt: inhaltRef.current,
        lokaleRevision: lokaleRevision.current,
        lokalGesichertAm: Date.now(),
        basis: {
          version: basisVersionRef.current,
          titel: seite.data?.titel ?? titelRef.current,
          inhalt: seite.data?.inhalt ?? "",
          fingerabdruck: `${basisVersionRef.current}`,
        },
      });
      setzeSpeicher((s) =>
        s === "konflikt" || s === "wird_gespeichert" ? s : "lokal_gesichert",
      );
    } catch {
      setzeSpeicher("nicht_sicher");
    }
  }, [personId, projektId, seiteId, basisVersion, seite.data]);

  const sendeServer = useCallback(async () => {
    if (speicher === "konflikt") return;
    if (speicherLaufend.current) {
      serverNeuPlanen.current = true;
      return;
    }
    if (!geraetId.current || !personId) return;
    speicherLaufend.current = true;
    setzeSpeicher("wird_gespeichert");
    const aenderungsId = crypto.randomUUID();
    const rev = lokaleRevision.current;
    const payload = {
      aenderungsId,
      lokaleRevision: rev,
      titel: titelRef.current,
      inhalt: inhaltRef.current,
      erwarteteVersion: basisVersionRef.current,
    };
    try {
      await schreibeEntwurf({
        schemaVersion: 1,
        personId,
        projektId,
        seiteId,
        geraetId: geraetId.current,
        titel: payload.titel,
        inhalt: payload.inhalt,
        lokaleRevision: rev,
        lokalGesichertAm: Date.now(),
        basis: {
          version: basisVersionRef.current,
          titel: seite.data?.titel ?? "",
          inhalt: seite.data?.inhalt ?? "",
          fingerabdruck: `${basisVersionRef.current}`,
        },
        ausstehend: {
          aenderungsId,
          lokaleRevision: rev,
          erwarteteVersion: payload.erwarteteVersion,
          titel: payload.titel,
          inhalt: payload.inhalt,
          fingerabdruck: `${basisVersionRef.current}`,
        },
      });
      await speichernMutation.mutateAsync(payload);
    } finally {
      speicherLaufend.current = false;
      if (serverNeuPlanen.current || rev !== lokaleRevision.current) {
        serverNeuPlanen.current = false;
        window.setTimeout(() => {
          void sendeServerRef.current?.();
        }, 0);
      }
    }
  }, [
    speicher,
    personId,
    projektId,
    seiteId,
    basisVersion,
    seite.data,
    speichernMutation,
  ]);
  sendeServerRef.current = sendeServer;

  const planeAutosave = useCallback(() => {
    if (lokalTimer.current) window.clearTimeout(lokalTimer.current);
    if (serverTimer.current) window.clearTimeout(serverTimer.current);

    lokalTimer.current = window.setTimeout(() => {
      void sichereLokal();
    }, AUTOSAVE.lokalTrailingMs);

    if (!lokalMaxTimer.current) {
      lokalMaxTimer.current = window.setTimeout(() => {
        lokalMaxTimer.current = null;
        void sichereLokal();
      }, AUTOSAVE.lokalMaxMs);
    }

    serverTimer.current = window.setTimeout(() => {
      void sendeServer();
    }, AUTOSAVE.serverTrailingMs);

    if (!serverMaxTimer.current) {
      serverMaxTimer.current = window.setTimeout(() => {
        serverMaxTimer.current = null;
        void sendeServer();
      }, AUTOSAVE.serverMaxMs);
    }
  }, [sichereLokal, sendeServer]);

  const onMarkdownChange = useCallback(
    (md: string) => {
      inhaltRef.current = md;
      setzeInhalt(md);
      lokaleRevision.current += 1;
      if (speicher !== "konflikt") setzeSpeicher("lokal_wird_gesichert");
      planeAutosave();
    },
    [planeAutosave, speicher],
  );

  useEffect(() => {
    return () => {
      if (lokalTimer.current) window.clearTimeout(lokalTimer.current);
      if (lokalMaxTimer.current) window.clearTimeout(lokalMaxTimer.current);
      if (serverTimer.current) window.clearTimeout(serverTimer.current);
      if (serverMaxTimer.current) window.clearTimeout(serverMaxTimer.current);
    };
  }, []);

  /*
   * `seiten.archivieren` und `wiederherstellen` gibt es seit Paket 3, einen
   * Knopf dafür bis zum 14.08.2026 nicht. Eine Seite, die niemand mehr
   * braucht, blieb damit für immer in der Liste.
   */
  const [archivFehler, setzeArchivFehler] = useState<string | null>(null);
  const archivieren = useMutation({
    /*
     * `mitUnterseiten: false` ist die vorsichtige Vorgabe. Hat die Seite noch
     * aktive Unterseiten, weist der Server ab und sagt es — besser, als eine
     * ganze Baumhälfte auf einen Klick verschwinden zu lassen.
     */
    mutationFn: () =>
      seite.data?.archiviertAm
        ? api.seiten.wiederherstellen.mutate({
            id: seiteId,
            erwarteteVersion: seite.data.version,
            mitUnterseiten: false,
          })
        : api.seiten.archivieren.mutate({
            id: seiteId,
            erwarteteVersion: seite.data?.version ?? 1,
            mitUnterseiten: false,
          }),
    onSuccess: async () => {
      setzeArchivFehler(null);
      await seite.refetch();
    },
    onError: (fehler) =>
      setzeArchivFehler(
        fehler instanceof Error ? fehler.message : "Nicht archiviert.",
      ),
  });

  const fassungErstellen = useMutation({
    mutationFn: () =>
      api.seiten.fassungErstellen.mutate({
        seiteId,
        erwarteteVersion: basisVersion,
      }),
    onSuccess: async () => {
      await client.invalidateQueries({
        queryKey: schluessel.seitenFassungen(seiteId),
      });
      await client.invalidateQueries({
        queryKey: schluessel.seitenVersionierungsstatus(seiteId),
      });
    },
  });

  const kannSchreiben = darfAendern(
    ich.data?.projekte.find((p) => p.projektId === projektId)?.rolle as
      | Rolle
      | undefined,
    { rechte: ich.data?.rechte },
  );
  const statistik = useMemo(() => seitenTextStatistik(inhalt), [inhalt]);
  const effektiverModus: SeitenModus = kannSchreiben ? modus : "lesen";
  const editierend = effektiverModus !== "lesen";

  if (seite.isLoading || !bereit) {
    return (
      <p className="px-6 py-14 font-mono text-[11.5px] text-tinte4" role="status">
        Seite wird geladen …
      </p>
    );
  }

  if (seite.error || !seite.data) {
    return (
      <p className="px-6 py-14 text-sm text-warn" role="alert">
        Seite nicht sichtbar oder nicht vorhanden.
      </p>
    );
  }

  const statusText: Record<SpeicherStatus, string> = {
    gespeichert: "Gespeichert",
    lokal_wird_gesichert: "Lokal wird gesichert …",
    lokal_gesichert: "Lokal gesichert",
    wird_gespeichert: "Wird gespeichert …",
    offline: "Offline – lokal gesichert",
    konflikt: "Konflikt – Entwurf bleibt erhalten",
    nicht_sicher: "Nicht sicher gespeichert",
  };

  /*
   * `flex-1` erbt die Höhe des Rollbereichs — siehe `SeitenStart`. Selbst
   * ausmessen darf diese Seite das Fenster nicht: Sie sitzt im Rollbereich der
   * Hülle, nicht im Fenster.
   */
  return (
    <div className="flex-1 bg-papier">
      <header className="border-b border-linie px-5 pt-6 sm:px-8 lg:px-12">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/projekte/$projektId/seiten"
            params={{ projektId }}
            className="font-mono text-[11.5px] text-akzent hover:underline"
          >
            ← {pfadProjektSeiten(projektId)}
          </Link>
          {kannSchreiben ? (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {editierend ? (
                <Button
                  variant="secondary"
                  className="h-[29px] px-3 font-mono text-[11.5px]"
                  onClick={() => {
                    void sichereLokal();
                    void sendeServer();
                  }}
                  disabled={speicher === "konflikt"}
                >
                  Jetzt speichern
                </Button>
              ) : null}
              <Button
                variant="secondary"
                className="h-[29px] px-3 font-mono text-[11.5px]"
                onClick={() => fassungErstellen.mutate()}
                disabled={fassungErstellen.isPending}
              >
                Fassung erstellen
              </Button>
              <Button
                variant={seite.data.archiviertAm ? "secondary" : "ghost"}
                className="h-[29px] px-3 font-mono text-[11.5px]"
                onClick={() => archivieren.mutate()}
                disabled={archivieren.isPending}
              >
                {seite.data.archiviertAm ? "Wiederherstellen" : "Archivieren"}
              </Button>
            </div>
          ) : null}
        </div>

        <div className="mt-7 max-w-[76rem]">
          <Mikrolabel grad={12} ton="leise">
            Wissensseite · Fassung {seite.data.version}
          </Mikrolabel>
          {editierend ? (
            <input
              className="mt-3 block w-full border-0 bg-transparent p-0 text-[clamp(2.2rem,5vw,3.65rem)] font-extralight leading-[1.08] tracking-[-0.04em] text-tinte shadow-none focus:ring-0 dark:bg-transparent"
              value={titel}
              disabled={speicher === "konflikt"}
              onChange={(e) => {
                setzeTitel(e.target.value);
                titelRef.current = e.target.value;
                lokaleRevision.current += 1;
                planeAutosave();
              }}
              aria-label="Seitentitel"
            />
          ) : (
            <h1 className="mt-3 text-[clamp(2.2rem,5vw,3.65rem)] font-extralight leading-[1.08] tracking-[-0.04em] text-tinte text-balance">
              {titel}
            </h1>
          )}
        </div>

        <Reiterleiste linie className="mt-8 min-w-0 overflow-x-auto">
          <Reiter
            aktiv={effektiverModus === "lesen"}
            onKlick={() => setzeModus("lesen")}
          >
            Lesen
          </Reiter>
          {kannSchreiben ? (
            <>
              <Reiter
                aktiv={effektiverModus === "bearbeiten"}
                onKlick={() => setzeModus("bearbeiten")}
              >
                Bearbeiten
              </Reiter>
              <Reiter
                aktiv={effektiverModus === "geteilt"}
                onKlick={() => setzeModus("geteilt")}
              >
                Geteilt
              </Reiter>
            </>
          ) : null}
          <span
            role="status"
            aria-live="polite"
            className="ml-auto whitespace-nowrap pb-3 font-mono text-[10.5px] text-tinte4"
          >
            {statusText[speicher]}
            {status.data?.letzteFassung
              ? ` · GitLab: ${status.data.letzteFassung.gitlabZustand}`
              : ""}
            {` · ${statistik.woerter} Wörter`}
          </span>
        </Reiterleiste>
      </header>

      <main className="px-5 py-8 sm:px-8 lg:px-12 lg:py-10">
        {banner ? (
          <div
            role="status"
            className="mb-6 border-l-2 border-warn bg-wisch px-4 py-3 text-sm text-tinte2"
          >
            {banner}
          </div>
        ) : null}
        {archivFehler ? (
          <p className="mb-6 text-sm text-warn" role="alert">
            {archivFehler}
          </p>
        ) : null}

        {editierend ? (
          <div
            className={
              effektiverModus === "geteilt"
                ? "grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)]"
                : "min-w-0"
            }
          >
            <SeitenEditor
              seiteId={seiteId}
              initialMarkdown={inhalt}
              onMarkdownChange={onMarkdownChange}
              lesend={speicher === "konflikt"}
            />
            {effektiverModus === "geteilt" ? (
              <section className="min-h-[560px] min-w-0 border border-linie bg-wisch">
                <div className="border-b border-linie px-5 py-3">
                  <Mikrolabel grad={11.5}>Live-Vorschau</Mikrolabel>
                </div>
                <div className="max-h-[calc(100dvh-17rem)] overflow-y-auto px-5 py-7 sm:px-8">
                  <SeitenLeseansicht markdown={inhalt} />
                </div>
              </section>
            ) : null}
          </div>
        ) : (
          <article className="mx-auto min-h-[24rem] w-full max-w-[860px] pb-16 pt-3">
            {inhalt.trim() ? (
              <SeitenLeseansicht markdown={inhalt} />
            ) : (
              <p className="font-mono text-[11.5px] text-tinte4">
                Diese Seite hat noch keinen Inhalt.
              </p>
            )}
          </article>
        )}
      </main>

      <section className="grid gap-12 border-t border-linie bg-wisch/40 px-5 py-10 sm:px-8 lg:px-12 xl:grid-cols-2">
        <aside className="min-w-0">
          <div className="flex items-baseline gap-2 border-b border-linie pb-3 font-mono text-[12.5px] font-medium text-tinte">
            Fassungen
            {fassungen.data && fassungen.data.length > 0 ? (
              <span className="font-mono text-[11px] text-tinte4">
                {fassungen.data.length}
              </span>
            ) : null}
          </div>
          {fassungen.data && fassungen.data.length === 0 ? (
            <p className="py-4 font-mono text-[11.5px] text-tinte4">
              Noch keine Fassungen.
            </p>
          ) : null}
          <ul role="list">
            {fassungen.data?.map((fassung) => (
              <li
                key={fassung.id}
                className="grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-linie py-3 font-mono text-[11px] text-tinte3"
              >
                <span>v{fassung.seitenVersion}</span>
                <span className="truncate">
                  {fassung.grund} · {fassung.gitlabZustand}
                </span>
                <time dateTime={fassung.erstelltAm}>
                  {new Date(fassung.erstelltAm).toLocaleDateString("de-DE")}
                </time>
              </li>
            ))}
          </ul>
        </aside>

        <div className="min-w-0">
          <div className="border-b border-linie pb-3 font-mono text-[12.5px] font-medium text-tinte">
            Kommentare
          </div>
          <div className="pt-4">
            <KommentareFaden
              projektId={projektId}
              betrifftArt="seite"
              betrifftId={seiteId}
              kannSchreiben={kannSchreiben}
              testId="seite-kommentare"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
